import pick from 'lodash/pick';

import {isOperatorNode, joinExpressions} from 'common/query-builder/cohorts-query-builder/util/expression-tree';
import {dataGroupIDsEqual} from 'common/util';

import {LogicalOperator, PredefinedCohort, Property} from '../enums/expression-enums';
import {ExpressionNode} from '../expression/expression-node';
import {OperandNode} from '../expression/operand-node';
import {OperatorNode} from '../expression/operator-node';
import {conditionalNegateExpression, expressionToClauseRoot} from '../expression/utils';
import {StagedPropertyFilterClause} from '../types/builder-state-types';
import {CohortId} from '../types/expression-types';
import {SerializedCohortClause} from '../types/serialized-staged-cohort-types';
import {Clause} from './clause';
import {PropertyFilter} from './property-filter';

interface CohortClauseOptions {
  cohortId: CohortId;
  isDependentCohort?: boolean;
  negated?: boolean;
  propertyFilterOperator?: LogicalOperator;
  propertyFilters?: PropertyFilter[];
  stagedPeoplePropertyFilter?: PropertyFilter;
}

export class CohortClause extends Clause {
  public static createDefault(): CohortClause {
    return new CohortClause({
      cohortId: PredefinedCohort.AllUsers,
    });
  }

  public static deserialize(options: SerializedCohortClause): CohortClause {
    return new CohortClause({
      ...pick(options, [`cohortId`, `isDependentCohort`, `negated`, `propertyFilterOperator`]),
      propertyFilters: options.propertyFilters.map((serializedPropClause) =>
        PropertyFilter.deserialize(serializedPropClause),
      ),
      stagedPeoplePropertyFilter: PropertyFilter.deserialize(options.stagedPeoplePropertyFilter),
    });
  }

  public static createFromParts(tree: ExpressionNode): CohortClause {
    let currNode = tree;
    let negated = false;
    if (isOperatorNode(tree) && tree.operator === LogicalOperator.Not) {
      currNode = tree.left;
      negated = true;
    }

    let propertyFiltersNode;
    if (isOperatorNode(currNode)) {
      propertyFiltersNode = currNode.right as OperatorNode;
      currNode = currNode.left;
    }

    const cohortNode = currNode as OperandNode;
    const {propertyFilters, propertyFilterOperator} = CohortClause.getPropertyFiltersAndOperator(propertyFiltersNode);
    return new CohortClause({
      cohortId: cohortNode.operand.value as CohortId,
      negated,
      propertyFilterOperator,
      propertyFilters,
    });
  }

  public declare readonly cohortId: CohortId;
  public declare readonly isDependentCohort: boolean;
  public declare readonly negated: boolean;
  // a property filter that is only ever staged, never committed. its purpose is to allow user to stage people properties on cohort clauses.
  // when the user commits the filter, the literal will convert from a cohort literal to a people literal.
  public declare readonly stagedPeoplePropertyFilter: PropertyFilter;

  constructor(options: CohortClauseOptions) {
    super(options);

    this.negated = options.negated || false;
    this.isDependentCohort = options.isDependentCohort || false;

    if (!options.stagedPeoplePropertyFilter) {
      this.stagedPeoplePropertyFilter = new PropertyFilter();
    }
  }

  public serialize(): SerializedCohortClause {
    return {
      ...pick(this, [`cohortId`, `isDependentCohort`, `negated`, `propertyFilterOperator`]),
      propertyFilters: this.getPropertyFilters().map((propertyFilter) => propertyFilter.serialize()),
      stagedPeoplePropertyFilter: this.stagedPeoplePropertyFilter.serialize(),
    };
  }

  public setCohortId({cohortId, isDependentCohort}: {cohortId: CohortId; isDependentCohort: boolean}): CohortClause {
    return this.newClause({cohortId, isDependentCohort});
  }

  public isNegated(): boolean {
    return this.negated;
  }

  public setNegated(negated: boolean): CohortClause {
    if (this.isNegated() === negated) {
      return this;
    }

    return this.newClause({negated});
  }

  public isValid(): boolean {
    return !this.isDependentCohort;
  }

  public addPropertyFilter(): CohortClause {
    return this.newClause({propertyFilters: this.getPropertyFilters().concat(new PropertyFilter())});
  }

  // @override
  public getStagedFilterClause(propertyFilterIdx: number | null): StagedPropertyFilterClause {
    if (propertyFilterIdx !== null) {
      return super.getStagedFilterClause(propertyFilterIdx);
    }

    return this.stagedPeoplePropertyFilter.getStagedFilterClause();
  }

  // @override
  public setStagedFilterClause(options: Partial<StagedPropertyFilterClause>, propertyFilterIdx: number | null): Clause {
    if (propertyFilterIdx !== null) {
      return super.setStagedFilterClause(options, propertyFilterIdx);
    }

    // user is editing people property on top-level clause, not existing or new property filter
    return this.newClause({stagedPeoplePropertyFilter: this.stagedPeoplePropertyFilter.setStagedFilterClause(options)});
  }

  public getExpressionTree(): OperatorNode {
    let expression = null;

    const cohortNode = new OperandNode({
      property: this.cohortId === PredefinedCohort.AllUsers ? Property.PredefinedCohort : Property.Cohort,
      value: this.cohortId,
    });
    const propertyFilters = this.getPropertyFilters();
    if (propertyFilters.length) {
      const propertyFilterTrees = propertyFilters.map((clause) => clause.getExpressionTree());
      const selectorNode = joinExpressions(propertyFilterTrees, this.propertyFilterOperator);
      expression = new OperatorNode(LogicalOperator.And, cohortNode, selectorNode);
    } else {
      expression = cohortNode;
    }

    return expressionToClauseRoot(conditionalNegateExpression(expression, this.negated));
  }

  public toTrackingData(): Obj {
    return {
      'clause cohort id': this.cohortId,
      'clause negated': this.isNegated(),
      'clause property filter operator': this.propertyFilterOperator,
      'clause property filters': this.getPropertyFilters().length,
      'clause valid': this.isValid(),
    };
  }

  public isInvalidForDataGroup({dataGroupId, cohorts = []}): boolean {
    if (this.cohortId === PredefinedCohort.AllUsers) {
      return this.getPropertyFilters().some((pf) => pf.isInvalidForDataGroup(dataGroupId));
    } else {
      const cohort = cohorts[this.cohortId];
      if (!cohort) {
        return true;
      }
      return !dataGroupIDsEqual(cohort.dataGroupId, dataGroupId);
    }
  }

  public removeInvalidPropertyFilters(dataGroupId): CohortClause {
    return this.newClause({
      propertyFilters: this.getPropertyFilters().filter((pf) => !pf.isInvalidForDataGroup(dataGroupId)),
    });
  }

  protected newClause(options: Partial<CohortClauseOptions> = {}): CohortClause {
    const oldOptions: CohortClauseOptions = {
      cohortId: this.cohortId,
      isDependentCohort: this.isDependentCohort,
      negated: this.negated,
      propertyFilterOperator: this.propertyFilterOperator,
      propertyFilters: this.propertyFilters,
    };
    return new CohortClause({...oldOptions, ...options});
  }
}
